import { ApiProvider } from "@api/ApiProvider";
import { queryClient } from "@api/queryClient";
import { RequireAuth } from "@components/auth/RequireAuth";
import { App } from "@containers/App";
import { GlobalModalRenderer } from "@shared/modal/GlobalModalRenderer";
import { ModalProvider } from "@shared/modal/ModalContext";
import { QueryClientProvider } from "@tanstack/react-query";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { HashRouter } from "react-router-dom";
import { checkForUpdate, forceReload } from "../versionCheck";
import { AuthProvider } from "./AuthContext";
import { migrateLegacySessionStorage } from "./migrateLegacySessionStorage";
import { ThemeProvider } from "./themes/ThemeProvider";

declare global {
  interface Window {
    __CONFIG__: { locale: string };
  }
}

window.__CONFIG__ = { locale: "en-US" };

// Move any pre-upgrade session state out of localStorage before first render,
// so hooks reading sessionStorage on mount see the migrated values.
migrateLegacySessionStorage();

// Start version check immediately — runs in parallel with Office initialization
const versionCheckPromise = checkForUpdate();

// service worker for diagram caching
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("./sw.js").catch(console.error);
}

const rootElement = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ApiProvider>
        <ThemeProvider>
          <ModalProvider>
            <RequireAuth>
              <HashRouter>{children}</HashRouter>
            </RequireAuth>
            <GlobalModalRenderer />
          </ModalProvider>
        </ThemeProvider>
      </ApiProvider>
    </AuthProvider>
  </QueryClientProvider>
);

/* Render application after Office initializes */
Office.onReady(async () => {
  const shouldReload = await versionCheckPromise;

  if (shouldReload) {
    forceReload();
    return;
  }

  root?.render(
    <AppProviders>
      <App />
    </AppProviders>
  );
});
